/**
 * Utilitários para formatação de datas
 * 
 * IMPORTANTE: O backend retorna datas em formato ISO (UTC).
 * Ao exibir apenas a data (sem hora), devemos usar UTC para evitar
 * que o timezone local altere o dia exibido.
 */

/**
 * Formata uma data do banco de dados para exibição no formato brasileiro (dd/mm/yyyy)
 * Usa UTC para evitar problemas de timezone ao exibir apenas a data
 * 
 * @param dateString - String de data em formato ISO do banco de dados
 * @returns Data formatada em pt-BR (dd/mm/yyyy)
 */
export function formatDateFromDB(dateString: string): string {
  // Adiciona 'Z' se não tiver para garantir que seja interpretado como UTC
  const dateStr = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  
  return new Date(dateStr).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Formata uma data e hora do banco de dados para exibição completa
 * 
 * @param dateString - String de data em formato ISO do banco de dados
 * @returns Data e hora formatada em pt-BR
 */
export function formatDateTimeFromDB(dateString: string): string {
  const dateStr = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  
  return new Date(dateStr).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
  });
}

/**
 * Converte uma data do input type="date" (YYYY-MM-DD) para o formato esperado pelo backend
 * 
 * @param dateString - String no formato YYYY-MM-DD
 * @returns String no formato ISO para envio ao backend
 */
export function formatDateForAPI(dateString: string): string {
  return dateString; // O backend já espera YYYY-MM-DD
}

/**
 * Extrai apenas a parte da data (YYYY-MM-DD) de uma string ISO para usar em input type="date"
 * 
 * @param dateString - String de data em formato ISO
 * @returns String no formato YYYY-MM-DD
 */
export function extractDateForInput(dateString: string): string {
  return dateString.split("T")[0];
}

/**
 * Retorna a data no formato YYYY-MM-DD no fuso local do navegador.
 * Usar `new Date().toISOString().slice(0, 10)` gera a data em UTC, o que,
 * em fusos atrasados (ex.: America/Sao_Paulo), produz a data de amanhã
 * entre 21h e 24h — causando erros como "Data não pode ser futura".
 *
 * @param date - Data base (padrão: agora)
 */
export function toLocalISODate(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
